<template>
  <div class="form-page">
    <header class="form-heading">
      <div>
        <p>Réglages</p>
        <h1>Notifications</h1>
        <span>Alertes de fin d’enchère sur téléphone et ordinateur.</span>
      </div>
    </header>

    <div class="form-content">
      <div class="form-panel">
        <!-- Cet appareil -->
        <section class="form-section">
          <div class="form-section__heading">
            <b>01</b>
            <div>
              <h2>Cet appareil</h2>
              <p>{{ thisDeviceLabel }}</p>
            </div>
          </div>
          <div class="form-section__body">
            <div
              v-if="!supported && ios && !standalone"
              class="notice notice--info"
            >
              <strong
                >Sur iPhone / iPad, installez d’abord l’application :</strong
              >
              <ol>
                <li>
                  Dans Safari, touchez le bouton <b>Partager</b> (carré avec une
                  flèche).
                </li>
                <li>
                  Choisissez <b>« Sur l’écran d’accueil »</b>, puis
                  <b>Ajouter</b>.
                </li>
                <li>
                  Ouvrez Prexens depuis l’icône de l’écran d’accueil et revenez
                  sur cette page.
                </li>
              </ol>
              <small>Nécessite iOS 16.4 ou plus récent.</small>
            </div>
            <div v-else-if="!supported" class="notice notice--warn">
              Ce navigateur ne permet pas les notifications push. Utilisez
              Chrome, Edge, Firefox ou Safari récent.
            </div>
            <div
              v-else-if="permissionState === 'denied'"
              class="notice notice--warn"
            >
              Les notifications sont bloquées pour ce site. Autorisez-les dans
              les réglages du navigateur (icône à gauche de l’adresse, ou
              Réglages → Notifications sur téléphone), puis rechargez la page.
            </div>
            <template v-else>
              <div class="device-state">
                <span class="dot" :class="{ on: subscribed }"></span>
                <strong>{{
                  subscribed
                    ? "Notifications activées sur cet appareil"
                    : "Notifications non activées sur cet appareil"
                }}</strong>
              </div>
              <div class="form-buttons">
                <button
                  v-if="!subscribed"
                  type="button"
                  class="form-button form-button--primary"
                  :disabled="busy"
                  @click="enable"
                >
                  {{ busy ? "Activation…" : "🔔 Activer les notifications" }}
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="form-button form-button--primary"
                    :disabled="busy || !thisDeviceId"
                    @click="sendTest(thisDeviceId, 10)"
                  >
                    Tester dans 10 s
                  </button>
                  <button
                    type="button"
                    class="form-button form-button--secondary"
                    :disabled="busy || !thisDeviceId"
                    @click="sendTest(thisDeviceId)"
                  >
                    Tester maintenant
                  </button>
                  <button
                    type="button"
                    class="form-button form-button--link form-button--danger"
                    :disabled="busy"
                    @click="disable"
                  >
                    Désactiver sur cet appareil
                  </button>
                </template>
              </div>
              <p v-if="ios && !standalone && subscribed" class="form-help">
                Astuce : installez l’application sur l’écran d’accueil pour un
                accès plus rapide.
              </p>
            </template>
            <p
              v-if="message"
              class="form-help"
              :class="messageIsError ? 'is-error' : 'form-success'"
            >
              {{ message }}
            </p>
          </div>
        </section>

        <!-- Appareils -->
        <section class="form-section">
          <div class="form-section__heading">
            <b>02</b>
            <div>
              <h2>Appareils qui reçoivent les alertes</h2>
              <p>Activez les notifications sur chaque appareil à alerter.</p>
            </div>
          </div>
          <div class="form-section__body">
            <p v-if="!devices.length" class="form-help">
              Aucun appareil pour l’instant.
            </p>
            <ul v-else class="devices">
              <li v-for="device in devices" :key="device.id">
                <div>
                  <strong
                    >{{ device.label
                    }}<em v-if="device.id === thisDeviceId">
                      — cet appareil</em
                    ></strong
                  >
                  <small>
                    Ajouté le {{ formatDate(device.created_at) }}
                    <template v-if="device.last_success_at">
                      · dernier envoi réussi le
                      {{ formatDate(device.last_success_at) }}</template
                    >
                  </small>
                  <small v-if="device.last_error" class="form-error"
                    >Dernière erreur : {{ device.last_error }}</small
                  >
                </div>
                <button
                  type="button"
                  class="form-button form-button--secondary form-button--small"
                  @click="sendTest(device.id)"
                >
                  Tester
                </button>
                <button
                  type="button"
                  class="form-button form-button--link form-button--danger"
                  @click="removeDevice(device)"
                >
                  Retirer
                </button>
              </li>
            </ul>
          </div>
        </section>

        <!-- Préférences -->
        <section class="form-section form-section--muted">
          <div class="form-section__heading">
            <b>03</b>
            <div>
              <h2>Alertes de fin d’enchère</h2>
              <p>
                Quand être prévenu avant la fin d’une enchère suivie (plusieurs
                choix possibles).
              </p>
            </div>
          </div>
          <div class="form-section__body">
            <div class="delays">
              <label
                v-for="delay in delays"
                :key="delay"
                class="delay"
                :class="{ active: prefs.alert_delays.includes(delay) }"
              >
                <input
                  type="checkbox"
                  :checked="prefs.alert_delays.includes(delay)"
                  @change="toggleDelay(delay)"
                />
                {{ delay === 60 ? "1 h avant" : `${delay} min avant` }}
              </label>
            </div>
            <p class="form-help">
              S’applique aux annonces ajoutées ou actualisées à partir de
              maintenant.
            </p>
            <div class="form-field">
              Canaux
              <label class="form-check">
                <input v-model="prefs.push_enabled" type="checkbox" />
                Notifications push (navigateur et téléphone)
              </label>
              <label class="form-check">
                <input v-model="prefs.ntfy_enabled" type="checkbox" />
                ntfy (ancien canal)
              </label>
            </div>
            <label v-if="prefs.ntfy_enabled" class="form-field">
              Sujet ntfy
              <input
                v-model.trim="prefs.ntfy_channel"
                class="form-input"
                placeholder="Nom du sujet ntfy"
              />
            </label>
          </div>
        </section>
        <section class="form-section form-section--muted">
          <div class="form-section__heading">
            <b>04</b>
            <div>
              <h2>Notifications de veille</h2>
              <p>
                Nouvelles annonces et baisses de prix de vos recherches. Elles
                restent toujours visibles dans la cloche, onglet « Veille ».
              </p>
            </div>
          </div>
          <div class="form-section__body">
            <div
              class="delays"
              role="radiogroup"
              aria-label="Veille sur le téléphone"
            >
              <label
                v-for="mode in MONITOR_PUSH_MODES"
                :key="mode.value"
                class="delay"
                :class="{ active: prefs.monitor_push === mode.value }"
              >
                <input
                  v-model="prefs.monitor_push"
                  type="radio"
                  name="monitor-push"
                  :value="mode.value"
                />
                {{ mode.label }}
              </label>
            </div>
            <p class="form-help">
              Sur le téléphone (et sur ntfy). Les alertes de fin d’enchère, elles,
              arrivent toujours avec le son. Sur iPhone, le système peut quand
              même jouer le son habituel en mode « Sans son ».
            </p>
          </div>
        </section>
        <footer class="form-footer">
          <span v-if="saved" class="form-footer__status form-success"
            >✓ Préférences enregistrées</span
          >
          <button
            type="button"
            class="form-button form-button--primary"
            :disabled="saving"
            @click="savePrefs"
          >
            {{ saving ? "Enregistrement…" : "Enregistrer les préférences" }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate as formatDateText } from "../clock";
import { computed, onMounted, reactive, ref } from "vue";
import axios from "axios";
import config from "@/config/config.js";
import {
  PUSH_DEVICE_ID_KEY,
  currentSubscription,
  deviceLabel,
  isIOS,
  isStandalone,
  permission,
  pushSupported,
  subscribeThisDevice,
  unsubscribeThisDevice,
} from "@/api/push.js";

const DEVICE_ID_KEY = PUSH_DEVICE_ID_KEY;
// Watch notifications on the phone (API: notifier.MONITOR_PUSH_MODES)
const MONITOR_PUSH_MODES = [
  { value: "on", label: "Oui" },
  { value: "silent", label: "Sans son" },
  { value: "off", label: "Non (seulement dans l’application)" },
];
const readDeviceId = () => {
  try {
    return localStorage.getItem(DEVICE_ID_KEY) || "";
  } catch {
    return "";
  }
};
const writeDeviceId = (value) => {
  try {
    if (value) localStorage.setItem(DEVICE_ID_KEY, value);
    else localStorage.removeItem(DEVICE_ID_KEY);
  } catch {
    /* stockage indisponible */
  }
};

export default {
  name: "NotificationsSettings",
  setup() {
    const supported = pushSupported();
    const ios = isIOS();
    const standalone = isStandalone();
    const thisDeviceLabel = deviceLabel();
    const permissionState = ref(permission());
    const subscribed = ref(false);
    const thisDeviceId = ref(readDeviceId());
    const devices = ref([]);
    const delays = ref([60, 30, 15, 10, 5, 2]);
    const prefs = reactive({
      alert_delays: [5],
      push_enabled: true,
      ntfy_enabled: true,
      ntfy_channel: "",
      monitor_push: "silent",
    });
    const busy = ref(false);
    const saving = ref(false);
    const saved = ref(false);
    const message = ref("");
    const messageIsError = ref(false);

    const say = (text, isError = false) => {
      message.value = text;
      messageIsError.value = isError;
    };
    const formatDate = (value) =>
      formatDateText(value, { dateStyle: "medium", timeStyle: "short" }, "—");

    const loadDevices = async () => {
      const { data } = await axios.get(
        `${config.apiUrl}/notifications/devices`,
      );
      devices.value = Array.isArray(data) ? data : [];
      // L'identifiant mémorisé ne correspond plus à rien : appareil retiré ailleurs
      if (
        thisDeviceId.value &&
        !devices.value.some((device) => device.id === thisDeviceId.value)
      ) {
        thisDeviceId.value = "";
        writeDeviceId("");
      }
    };

    const load = async () => {
      try {
        const [{ data: prefData }, { data: pushConfig }] = await Promise.all([
          axios.get(`${config.apiUrl}/notifications/preferences`),
          axios.get(`${config.apiUrl}/notifications/config`),
        ]);
        Object.assign(prefs, prefData);
        if (Array.isArray(pushConfig?.alert_delays))
          delays.value = pushConfig.alert_delays;
        if (!pushConfig?.push_available)
          say(
            "Les notifications push ne sont pas encore configurées sur le serveur.",
            true,
          );
        await loadDevices();
      } catch (error) {
        console.error("Chargement des notifications impossible", error);
        say("Les réglages n’ont pas pu être chargés.", true);
      }
      if (supported)
        subscribed.value =
          Boolean(await currentSubscription()) && Boolean(thisDeviceId.value);
    };

    const enable = async () => {
      busy.value = true;
      say("");
      try {
        const device = await subscribeThisDevice();
        thisDeviceId.value = device.id;
        writeDeviceId(device.id);
        subscribed.value = true;
        permissionState.value = permission();
        await loadDevices();
        say("Notifications activées. Envoyez un test pour vérifier.");
      } catch (error) {
        permissionState.value = permission();
        say(
          error.response?.data?.error ||
            error.message ||
            "L’activation a échoué.",
          true,
        );
      } finally {
        busy.value = false;
      }
    };

    const disable = async () => {
      busy.value = true;
      try {
        await unsubscribeThisDevice(thisDeviceId.value);
        thisDeviceId.value = "";
        writeDeviceId("");
        subscribed.value = false;
        await loadDevices();
        say("Notifications désactivées sur cet appareil.");
      } catch (error) {
        say("La désactivation a échoué.", true);
      } finally {
        busy.value = false;
      }
    };

    // delay : le temps de verrouiller le téléphone ou de quitter l'app, comme
    // pour une vraie alerte (app ouverte, le téléphone peut ne pas sonner)
    const sendTest = async (deviceId, delay = 0) => {
      say("");
      try {
        await axios.post(
          `${config.apiUrl}/notifications/devices/${deviceId}/test`,
          null,
          { params: delay ? { delay } : {} },
        );
        say(
          delay
            ? `Test programmé : verrouillez votre téléphone ou quittez l’application maintenant. La notification arrive dans ${delay} secondes.`
            : "Notification de test envoyée : elle doit apparaître dans quelques secondes.",
        );
      } catch (error) {
        say(
          "L’envoi du test a échoué : l’appareil a peut-être été désabonné. Réactivez les notifications sur cet appareil.",
          true,
        );
      }
      await loadDevices();
    };

    const removeDevice = async (device) => {
      if (!window.confirm(`Ne plus envoyer d’alertes à « ${device.label} » ?`))
        return;
      if (device.id === thisDeviceId.value) return disable();
      await axios.delete(`${config.apiUrl}/notifications/devices/${device.id}`);
      await loadDevices();
    };

    const toggleDelay = (delay) => {
      const selected = new Set(prefs.alert_delays);
      if (selected.has(delay)) {
        if (selected.size === 1) return; // au moins une alerte
        selected.delete(delay);
      } else selected.add(delay);
      prefs.alert_delays = [...selected].sort((a, b) => b - a);
    };

    const savePrefs = async () => {
      saving.value = true;
      saved.value = false;
      try {
        const { data } = await axios.put(
          `${config.apiUrl}/notifications/preferences`,
          { ...prefs },
        );
        Object.assign(prefs, data);
        saved.value = true;
      } catch (error) {
        say("Les préférences n’ont pas pu être enregistrées.", true);
      } finally {
        saving.value = false;
      }
    };

    onMounted(load);
    return {
      MONITOR_PUSH_MODES,
      busy,
      delays,
      devices,
      disable,
      enable,
      formatDate,
      ios,
      message,
      messageIsError,
      permissionState,
      prefs,
      removeDevice,
      savePrefs,
      saved,
      saving,
      sendTest,
      standalone,
      subscribed,
      supported: computed(() => supported),
      thisDeviceId,
      thisDeviceLabel,
      toggleDelay,
    };
  },
};
</script>

<style scoped>
.notice {
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
}
.notice ol {
  margin: 8px 0;
  padding-left: 20px;
}
.notice--info {
  background: #edf4fb;
  color: #24486e;
}
.notice--warn {
  background: #fff5e8;
  color: #7a4a10;
}
.device-state {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c0c8d2;
}
.dot.on {
  background: #27a979;
  box-shadow: 0 0 0 4px #e1f5ed;
}
.devices {
  margin: 0;
  padding: 0;
  list-style: none;
}
.devices li {
  display: flex;
  margin: 0;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #edf0f4;
}
.devices li:first-child {
  padding-top: 0;
  border-top: 0;
}
.devices li > div {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.devices strong {
  font-size: 13px;
}
.devices em {
  color: #1f7a5a;
  font-style: normal;
  font-weight: 600;
}
.devices small {
  color: #7a8697;
  font-size: 12px;
}
.delays {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.delay {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #d5dde5;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.delay.active {
  border-color: #168a64;
  background: #eaf7f2;
  color: #196b51;
}
.delay input {
  margin: 0;
}
.form-check {
  font-weight: 400;
}
@media (max-width: 640px) {
  .devices li {
    flex-wrap: wrap;
  }
}
</style>
